import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, of, timer } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { GeocodeCacheEntry, LatLng } from 'src/app/main';

interface NominatimSearchResult {
    lat: string;
    lon: string;
}

interface GeocodeJob {
    key: string;
    address: string;
    subject: ReplaySubject<LatLng | null>;
}

@Injectable({
    providedIn: 'root'
})
export class GeocodingService {
    private static readonly nominatimUrl = 'https://nominatim.openstreetmap.org/search';
    private static readonly storageKey = 'geocode-cache';
    private static readonly ttlMs = 30 * 24 * 60 * 60 * 1000;
    private static readonly minIntervalMs = 1000;

    private readonly http: HttpClient;
    private readonly memoryCache = new Map<string, GeocodeCacheEntry>();
    private readonly pending = new Map<string, ReplaySubject<LatLng | null>>();
    private readonly queue: GeocodeJob[] = [];
    private lastRequestAt = 0;
    private draining = false;

    constructor(httpBackend: HttpBackend) {
        this.http = new HttpClient(httpBackend);
        this.hydrateFromStorage();
    }

    resolve(address: string): Observable<LatLng | null> {
        const key = GeocodingService.normalize(address);
        if (!key) {
            return of(null);
        }

        const cached = this.readCache(key);
        if (cached !== undefined) {
            return of(cached);
        }

        const pending = this.pending.get(key);
        if (pending) {
            return pending.asObservable().pipe(take(1));
        }

        const subject = new ReplaySubject<LatLng | null>(1);
        this.pending.set(key, subject);
        this.queue.push({ key, address: address.trim(), subject });
        this.drain();
        return subject.asObservable().pipe(take(1));
    }

    private drain(): void {
        if (this.draining || this.queue.length === 0) {
            return;
        }

        this.draining = true;
        const job = this.queue.shift();
        if (!job) {
            this.draining = false;
            return;
        }
        const cached = this.readCache(job.key);
        if (cached !== undefined) {
            this.finish(job, cached);
            this.draining = false;
            this.drain();
            return;
        }

        this.throttledFetch(job.address).subscribe({
            next: value => {
                if (value) {
                    this.writeCache(job.key, value);
                    this.finish(job, value);
                    this.draining = false;
                    this.drain();
                    return;
                }

                const stripped = GeocodingService.stripDiacritics(job.address);
                if (stripped === job.address) {
                    this.writeCache(job.key, null);
                    this.finish(job, null);
                    this.draining = false;
                    this.drain();
                    return;
                }

                // Not found — some OSM entries are tagged without diacritics even when the
                // registered address uses correct Portuguese spelling (e.g. "Jeronimo" vs
                // "Jerônimo"). One extra throttled retry before giving up.
                this.throttledFetch(stripped).subscribe({
                    next: fallbackValue => {
                        this.writeCache(job.key, fallbackValue);
                        this.finish(job, fallbackValue);
                        this.draining = false;
                        this.drain();
                    },
                    error: () => {
                        this.finish(job, null);
                        this.draining = false;
                        this.drain();
                    }
                });
            },
            error: () => {
                this.finish(job, null);
                this.draining = false;
                this.drain();
            }
        });
    }

    private throttledFetch(address: string): Observable<LatLng | null> {
        const wait = Math.max(0, GeocodingService.minIntervalMs - (Date.now() - this.lastRequestAt));
        return timer(wait).pipe(
            switchMap(() => {
                this.lastRequestAt = Date.now();
                return this.fetchNominatim(address);
            })
        );
    }

    private fetchNominatim(address: string): Observable<LatLng | null> {
        const params = new HttpParams()
            .set('format', 'json')
            .set('limit', '1')
            .set('q', address);

        return this.http.get<NominatimSearchResult[]>(GeocodingService.nominatimUrl, { params }).pipe(
            map(results => GeocodingService.parseResult(results))
        );
    }

    private finish(job: GeocodeJob, value: LatLng | null): void {
        job.subject.next(value);
        job.subject.complete();
        this.pending.delete(job.key);
    }

    private readCache(key: string): LatLng | null | undefined {
        const memory = this.memoryCache.get(key);
        if (memory && !GeocodingService.isExpired(memory)) {
            return memory.value;
        }

        if (memory) {
            this.memoryCache.delete(key);
        }

        return undefined;
    }

    private writeCache(key: string, value: LatLng | null): void {
        const entry: GeocodeCacheEntry = { key, value, ts: Date.now() };
        this.memoryCache.set(key, entry);
        this.persist();
    }

    private hydrateFromStorage(): void {
        try {
            const raw = localStorage.getItem(GeocodingService.storageKey);
            if (!raw) {
                return;
            }

            const parsed = JSON.parse(raw) as Record<string, GeocodeCacheEntry>;
            Object.keys(parsed || {}).forEach(key => {
                const entry = parsed[key];
                if (!entry || GeocodingService.isExpired(entry)) {
                    return;
                }
                this.memoryCache.set(key, entry);
            });
        } catch {
            this.memoryCache.clear();
        }
    }

    private persist(): void {
        try {
            const payload: Record<string, GeocodeCacheEntry> = {};
            this.memoryCache.forEach((entry, key) => {
                if (!GeocodingService.isExpired(entry)) {
                    payload[key] = entry;
                }
            });
            localStorage.setItem(GeocodingService.storageKey, JSON.stringify(payload));
        } catch {
        }
    }

    private static parseResult(results: NominatimSearchResult[]): LatLng | null {
        const first = results?.[0];
        const lat = Number(first?.lat);
        const lon = Number(first?.lon);
        if (!first || !isFinite(lat) || !isFinite(lon)) {
            return null;
        }
        return { lat, lon };
    }

    private static normalize(address: string): string {
        return (address || '').trim().toLowerCase().replace(/\s+/g, ' ');
    }

    private static stripDiacritics(address: string): string {
        return address.normalize('NFD').replace(/[̀-ͯ]/g, '');
    }

    private static isExpired(entry: GeocodeCacheEntry): boolean {
        return !entry?.ts || (Date.now() - entry.ts) > GeocodingService.ttlMs;
    }
}
